import { Link } from "react-router-dom";
import { useProtocolHealth } from "../hooks/useProtocolHealth.js";

/**
 * Sticky banner shown when the BondContract has new deposits disabled
 * (either DePegGuard tripped per §13 or governance manually paused per
 * §3.1). Existing positions, withdrawals, lending and treasury
 * execution all remain live — only NEW deposits are blocked — so we
 * spell that out in the body to head off the "is my money safe?"
 * support question.
 *
 * Interest-reserve breaches are NOT surfaced in this banner. Per client
 * feedback: there is no action the end-user can take on a coverage
 * breach (Marketing/Ops extraction halt is the protocol's own §11.7
 * response, governance-level decision), so a red sticky banner just
 * panics readers without giving them a path. The same status lives on
 * the Protocol Health page where it sits next to reserve-bucket charts,
 * coverage curves, and the operator runbook — the right surface for
 * the audience that actually acts on it.
 *
 * The banner reads from the cached `useProtocolHealth` snapshot so
 * every page sharing the hook gets the same value without re-fetching.
 */
export default function PausedDepositsBanner() {
    const { data } = useProtocolHealth();

    if (!data) return null;

    const paused = Boolean(data.depositsPaused);

    if (!paused) return null;

    return (
        <div className="flex flex-col">
            <PausedBanner />
        </div>
    );
}

function PausedBanner() {
    return (
        <div
            role="alert"
            aria-live="polite"
            className="bg-[#FBC206] text-[#10281b] border-b border-[#10281b]/15"
        >
            <div className="mx-auto flex max-w-7xl items-start gap-3 px-4 py-2.5 sm:px-8">
                <PauseIcon />
                <div className="flex-1 min-w-0">
                    <div className="font-manrope text-[12.5px] font-bold">
                        New Term Deposits are paused
                    </div>
                    <p className="mt-0.5 text-[11px] leading-snug text-[#10281b]/85">
                        New Term Deposits are temporarily paused by a protocol
                        safety control. Existing positions keep accruing;
                        withdrawals, lending and repayments remain available.
                        See Protocol Health for the current reason.
                    </p>
                </div>
                <Link
                    to="/protocol-health"
                    className="hidden sm:inline-flex shrink-0 items-center rounded-full bg-[#10281b] px-3 py-1 text-[10.5px] font-semibold text-[#FBC206] hover:bg-[#0E2C19]"
                >
                    Protocol Health
                </Link>
            </div>
        </div>
    );
}

function PauseIcon() {
    return (
        <svg
            className="mt-0.5 h-5 w-5 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="10" />
            <line x1="10" y1="9" x2="10" y2="15" />
            <line x1="14" y1="9" x2="14" y2="15" />
        </svg>
    );
}

